import React from "react";
import { StepDefinition, AttachmentDto } from "../../../../types/types";
import {
  renderSingleField,
  renderNestedGroup,
} from "../dealFormFields";
import { isPayoutPoliciesStep, isNotesStep } from "../../../../utils/deals/dealFormStepHelpers";
import { FighterSelect } from "./fighterSelect";
import { PayoutPoliciesStep } from "./payoutPoliciesStep";
import { NotesStep } from "./notesStep";
import { DealFormData, FieldHelpers, FormValue, TemplateStep } from "../dealFormTypes";
import { TranslationFn } from "../dealFormFields";

export type RegularStepProps = {
  step: StepDefinition;
  stepId: string;
  formData: DealFormData;
  fieldHelpers: FieldHelpers;
  t: TranslationFn;
  updateFormAtPath: (path: string[], value: FormValue) => void;
  isGroupChat: boolean;
  isFirstStep: boolean;
  selectedFighterId: string | null;
  setSelectedFighterId: React.Dispatch<React.SetStateAction<string | null>>;
  availableFighters: Array<{ id: string; firstName?: string; lastName?: string }>;
  errors: Record<string, string | undefined>;
  setErrors: React.Dispatch<React.SetStateAction<Record<string, string | undefined>>>;
  selectedFiles: File[];
  setSelectedFiles: React.Dispatch<React.SetStateAction<File[]>>;
  existingAttachments: AttachmentDto[];
  attachmentsToRemove: string[];
  setAttachmentsToRemove: React.Dispatch<React.SetStateAction<string[]>>;
  uploadingFiles: boolean;
  isDragging: boolean;
  setIsDragging: React.Dispatch<React.SetStateAction<boolean>>;
  dragCounter: number;
  setDragCounter: React.Dispatch<React.SetStateAction<number>>;
  rawTemplateSteps: TemplateStep[];
};

export const RegularStep = ({
  step,
  stepId,
  formData,
  fieldHelpers,
  t,
  updateFormAtPath,
  isGroupChat,
  isFirstStep,
  selectedFighterId,
  setSelectedFighterId,
  availableFighters,
  errors,
  setErrors,
  selectedFiles,
  setSelectedFiles,
  existingAttachments,
  attachmentsToRemove,
  setAttachmentsToRemove,
  uploadingFiles,
  isDragging,
  setIsDragging,
  dragCounter,
  setDragCounter,
  rawTemplateSteps,
}: RegularStepProps): React.ReactElement | null => {
  const fields = step.fields ?? [];

  if (isPayoutPoliciesStep(step)) {
    return (
      <PayoutPoliciesStep
        step={step}
        stepId={stepId}
        formData={formData}
        fieldHelpers={fieldHelpers}
        t={t}
        updateFormAtPath={updateFormAtPath}
        rawTemplateSteps={rawTemplateSteps}
      />
    );
  }

  if (fields.length === 0) {
    if (isNotesStep(step))
      return (
        <NotesStep
          step={step}
          stepId={stepId}
          formData={formData}
          fieldHelpers={fieldHelpers}
          t={t}
          selectedFiles={selectedFiles}
          setSelectedFiles={setSelectedFiles}
          existingAttachments={existingAttachments}
          attachmentsToRemove={attachmentsToRemove}
          setAttachmentsToRemove={setAttachmentsToRemove}
          uploadingFiles={uploadingFiles}
          isDragging={isDragging}
          setIsDragging={setIsDragging}
          dragCounter={dragCounter}
          setDragCounter={setDragCounter}
        />
      );

    return (
      <div className="flex flex-col items-center justify-center py-10">
        <p className="text-[13px] text-slate-400 font-medium">
          {t("noFieldsDefinedForStep")}
        </p>
      </div>
    );
  }

  const normalFields = fields.filter(
    (f) => (f.type || "text").toLowerCase() !== "textarea",
  );
  const textareaFields = fields.filter(
    (f) => (f.type || "text").toLowerCase() === "textarea",
  );

  return (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-x-6 gap-y-4 items-start">
      {normalFields.map((f) => {
        if (Array.isArray(f.fields) && f.fields.length > 0) {
          return renderNestedGroup(
            stepId,
            f,
            (formData[stepId] ?? {}) as Record<string, FormValue>,
            fieldHelpers,
          );
        }
        return (
          <React.Fragment key={f.key}>
            {renderSingleField(
              stepId,
              f,
              (formData[stepId] ?? {}) as Record<string, FormValue>,
              undefined,
              undefined,
              fieldHelpers,
            )}
          </React.Fragment>
        );
      })}
      {isGroupChat && isFirstStep && (
        <FighterSelect
          isGroupChat={isGroupChat}
          selectedFighterId={selectedFighterId}
          setSelectedFighterId={setSelectedFighterId}
          availableFighters={availableFighters}
          errors={errors}
          setErrors={setErrors}
          t={t}
        />
      )}
      {textareaFields.map((f) => (
        <React.Fragment key={f.key}>
          {renderSingleField(
            stepId,
            f,
            (formData[stepId] ?? {}) as Record<string, FormValue>,
            undefined,
            undefined,
            fieldHelpers,
          )}
        </React.Fragment>
      ))}
    </div>
  );
};
